'use client';

import React, { useState, useRef, useCallback } from 'react'

/*
import { Button } from "@/components/catalyst/button";
import { Dialog, DialogActions, DialogBody, DialogDescription, DialogTitle } from "@/components/catalyst/dialog";
import { Avatar } from "@/components/catalyst/avatar";
*/

import ReactCrop, {
    centerCrop,
    makeAspectCrop,
    Crop,
    PixelCrop,
} from 'react-image-crop'
import { canvasPreview } from './canvasPreview'
import { useDebounceEffect } from './useDebounceEffect'
import useLocalizedCopy from '../../hooks/useLocalizedCopy'

import 'react-image-crop/dist/ReactCrop.css'

function Avatar({ src, onClick }: { src: string, onClick?: () => void }) {
    return <div onClick={onClick} className="cursor-pointer">
        <img src={src} className="hover:z-5 relative shrink-0 rounded-full ring-1 ring-light-light size-24" alt="avatar" />
    </div>
}

// This is to demonstate how to make and center a % aspect crop
// which is a bit trickier so we use some helper functions.
function centerAspectCrop(
    mediaWidth: number,
    mediaHeight: number,
    aspect: number,
) {
    return centerCrop(
        makeAspectCrop(
            {
                unit: '%',
                width: 90,
            },
            aspect,
            mediaWidth,
            mediaHeight,
        ),
        mediaWidth,
        mediaHeight,
    )
}

export default function ModalDeceasedPersonPhotoCropper({
    setPhotoBlob,
    currentPhotoUrl = undefined
}: {
    setPhotoBlob: (blob: Blob) => void, currentPhotoUrl?: string
}) {
    const { copy } = useLocalizedCopy();
    const fileInputRef = useRef<HTMLInputElement>(null)

    const [imgSrc, setImgSrc] = useState('')
    const [cropDialogIsOpen, setCropDialogIsOpen] = useState(false)

    const previewCanvasRef = useRef<HTMLCanvasElement>(null)
    const imgRef = useRef<HTMLImageElement>(null)
    const hiddenAnchorRef = useRef<HTMLAnchorElement>(null)
    const blobUrlRef = useRef('')
    const [crop, setCrop] = useState<Crop>()
    const [completedCrop, setCompletedCrop] = useState<PixelCrop>()
    const [scale, setScale] = useState(1)
    const [rotate, setRotate] = useState(0)
    const [aspect, setAspect] = useState<number | undefined>(1 / 1)

    function onSelectFile(e: React.ChangeEvent<HTMLInputElement>) {
        if (e.target.files && e.target.files.length > 0) {
            setCrop(undefined) // Makes crop preview update between images.
            const reader = new FileReader()
            reader.addEventListener('load', () => {
                setImgSrc(reader.result?.toString() || '')
                setCropDialogIsOpen(true)
            })
            reader.readAsDataURL(e.target.files[0])
        }
    }

    function onImageLoad(e: React.SyntheticEvent<HTMLImageElement>) {
        if (aspect) {
            const { width, height } = e.currentTarget
            setCrop(centerAspectCrop(width, height, aspect))
        }
    }

    const onSetPhotoUrl = useCallback(async () => {
        const image = imgRef.current
        const previewCanvas = previewCanvasRef.current
        if (!image || !previewCanvas || !completedCrop) {
            throw new Error('Crop canvas does not exist')
        }

        // This will size relative to the uploaded image
        // size. If you want to size according to what they
        // are looking at on screen, remove scaleX + scaleY
        const scaleX = image.naturalWidth / image.width
        const scaleY = image.naturalHeight / image.height

        const offscreen = new OffscreenCanvas(
            completedCrop.width * scaleX,
            completedCrop.height * scaleY,
        )
        const ctx = offscreen.getContext('2d')
        if (!ctx) {
            throw new Error('No 2d context')
        }

        ctx.drawImage(
            previewCanvas,
            0,
            0,
            previewCanvas.width,
            previewCanvas.height,
            0,
            0,
            offscreen.width,
            offscreen.height,
        )
        // You might want { type: "image/jpeg", quality: <0 to 1> } to
        // reduce image size
        const blob = await offscreen.convertToBlob({
            type: 'image/png',
        })
        setPhotoBlob(blob)

    }, [completedCrop, setPhotoBlob])

    useDebounceEffect(
        async () => {
            if (
                completedCrop?.width &&
                completedCrop?.height &&
                imgRef.current &&
                previewCanvasRef.current
            ) {
                // We use canvasPreview as it's much faster than imgPreview.
                canvasPreview(
                    imgRef.current,
                    previewCanvasRef.current,
                    completedCrop,
                    scale,
                    rotate,
                )
            }
        },
        100,
        [completedCrop, scale, rotate],
    )

    const onCancelUseImage = useCallback(() => {
        setCrop(undefined)
        setCompletedCrop(undefined)
        setImgSrc('')
        setCropDialogIsOpen(false)
        fileInputRef.current!.value = ''
    }, [setCropDialogIsOpen])

    const onUseImage = useCallback(() => {
        setCropDialogIsOpen(false)
        onSetPhotoUrl()
    }, [setCropDialogIsOpen, onSetPhotoUrl])

    const onStartEditingAvatar = useCallback(() => {
        setShowFileInput(true)
        setTimeout(() => {
            fileInputRef.current?.click()
        }, 100)
    }, [])

    const [showFileInput, setShowFileInput] = useState(!currentPhotoUrl)

    return (
        <div>
            <>
                {cropDialogIsOpen ? (
                    <>
                        <div
                            className="justify-center items-center flex overflow-x-hidden overflow-y-auto fixed inset-0 z-50 outline-none focus:outline-none"
                        >
                            <div className="relative w-auto my-6 mx-auto max-w-3xl">
                                {/*content*/}
                                <div className="border-0 rounded-lg shadow-lg relative flex flex-col w-full bg-white outline-none focus:outline-none">
                                    {/*header*/}
                                    <div className="flex items-start justify-between p-5 border-b border-solid border-blueGray-200 rounded-t">
                                        <h3 className="text-xl font-semibold">
                                            {copy.Common.buttons.edit} {copy.Services.view.photos}
                                        </h3>
                                    </div>
                                    {/*body*/}
                                    <div className="relative p-6 flex-auto">
                                        <div style={{ maxHeight: '50vh', overflowY: 'auto', overflowX: 'hidden' }}>
                                            {!!(imgSrc || currentPhotoUrl) && (
                                                <ReactCrop
                                                    crop={crop}
                                                    onChange={(_, percentCrop) => setCrop(percentCrop)}
                                                    onComplete={(c) => setCompletedCrop(c)}
                                                    aspect={aspect}
                                                    // minWidth={400}
                                                    minHeight={100}
                                                // circularCrop
                                                >
                                                    {/* eslint-disable-next-line @next/next/no-img-element */}
                                                    <img
                                                        ref={imgRef}
                                                        alt="Select crop"
                                                        src={imgSrc || currentPhotoUrl}
                                                        style={{ transform: `scale(${scale}) rotate(${rotate}deg)` }}
                                                        crossOrigin="anonymous"
                                                        onLoad={onImageLoad}
                                                    />
                                                </ReactCrop>
                                            )}
                                        </div>
                                    </div>
                                    {/*footer*/}
                                    <div className="flex items-center justify-end p-6 border-t border-solid border-blueGray-200 rounded-b">
                                        <button
                                            className="btn btn-sm btn-light me-2"
                                            type="button"
                                            onClick={() => {
                                                onCancelUseImage()
                                            }}
                                        >
                                            {copy.Common.buttons.cancel}
                                        </button>
                                        <button
                                            className="btn btn-sm btn-primary"
                                            type="button"
                                            onClick={() => onUseImage()}
                                        >
                                            {copy.Common.buttons.save}
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="opacity-25 fixed inset-0 z-40 bg-black"></div>
                    </>
                ) : null}
            </>
            {!!completedCrop ? (
                <>
                    <div className='mt-4'>
                        <canvas
                            ref={previewCanvasRef}
                            style={{
                                border: '1px solid black',
                                objectFit: 'contain',
                                width: 100,
                                height: 100,
                                borderRadius: '50%',
                            }}
                            onClick={() => {
                                setCropDialogIsOpen(true)
                            }}
                        />
                    </div>
                </>
            ) : <>
                {currentPhotoUrl && <div className="mt-4">
                    <Avatar
                        src={currentPhotoUrl}
                        onClick={() => {
                            // onStartEditingAvatar()
                            setCropDialogIsOpen(true)
                        }}
                    />
                </div>}
            </>}
            {!showFileInput && <div className="mt-4">
                <div
                    className="text-blue-500 cursor-pointer text-sm"
                    onClick={() => {
                        onStartEditingAvatar()
                    }}
                >
                    {copy.Common.buttons.edit}
                </div>
            </div>}
            {showFileInput && <div className="Crop-Controls mt-2">
                <input ref={fileInputRef} type="file" accept="image/*" onChange={onSelectFile} />
            </div>}
        </div>
    )
}